import { faBalanceScaleRight, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@ui';
import { Column, DataTable, DataTableContainer, TableActionButton } from 'components/DataTable';
import { useModal } from 'hooks/useModal';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '~/hooks/useApi';
import { useConfirmedCallback } from '~/hooks/useConfirmedCallback';
import { PermissionForm } from '../PermissionForm';
import { PermissionGroupForm } from '../PermissionGroupForm';
import { PermissionGroup } from '../types';

interface Props {
    clubSlug: string;
}

export const PermissionGroupList: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
    clubSlug
}) => {
    const { t } = useTranslation('general');
    const api = useApi();
    const [setModal, resetModal] = useModal();
    const asyncListRef = useRef(null);
    const columns = useMemo(
        (): Column[] => [
            { uid: 'title', name: t('permissions.groupManagement.columns.title') },
            { uid: 'actions', name: t('permissions.groupManagement.columns.actions'), align: 'end' }
        ],
        [t]
    );

    const showPermissionForm = (permissionGroup: PermissionGroup) =>
        setModal({
            title: t('permissions.groupManagement.manageRights'),
            content: (
                <PermissionForm
                    permissionGroup={permissionGroup}
                    clubSlug={clubSlug}
                    callback={() => {
                        resetModal();
                        asyncListRef.current?.reload();
                    }}
                />
            ),
            show: true,
            size: 50
        });

    const showPermissionGroupForm = (permissionGroup?: PermissionGroup) =>
        setModal({
            title: permissionGroup
                ? t('permissions.groupManagement.editGroup')
                : t('permissions.groupManagement.newGroup'),
            content: (
                <PermissionGroupForm
                    clubSlug={clubSlug}
                    permissionGroup={permissionGroup}
                    callback={() => {
                        asyncListRef.current?.reload();
                        resetModal();
                    }}
                />
            ),
            show: true
        });

    const handleDeletePermissionGroup = useConfirmedCallback((permissionGroup: PermissionGroup) => {
        void api({
            url: `permissionGroup/${permissionGroup.id}`,
            method: 'DELETE',
            onSuccess: () => asyncListRef.current?.remove(permissionGroup.id)
        });
    });

    return (
        <DataTableContainer>
            <Button onPress={() => showPermissionGroupForm()}>{t('permissions.groupManagement.newGroup')}</Button>
            <DataTable
                asyncListRef={asyncListRef}
                fetchUrl={`club/${clubSlug}/permissionGroup`}
                columns={columns}
                rowActions={(permissionGroup) => (
                    <TableActionButton.Container justify="flex-end">
                        <TableActionButton
                            icon={faBalanceScaleRight}
                            tooltip={t('permissions.groupManagement.manageRights')}
                            onClick={() => showPermissionForm(permissionGroup)}
                        />
                        <TableActionButton
                            icon={faEdit}
                            color="warning"
                            tooltip={t('permissions.groupManagement.edit')}
                            onClick={() => showPermissionGroupForm(permissionGroup)}
                        />
                        <TableActionButton
                            icon={faTrash}
                            color="error"
                            tooltip={t('permissions.groupManagement.delete')}
                            onClick={() => handleDeletePermissionGroup(permissionGroup)}
                        />
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};
